import {
    AllEvents,
    CardBreakpointRatios,
    CardItem,
    CollectionAvailableEventProps,
    createCollectionAvailableEvent,
    FixedRatio,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React, { Fragment, ReactNode } from 'react'
import { CommonCardProps } from '../../cards/CardItem.Props'
import {
    DisplayOnBreakpoint,
    StyledGridItem,
    StyledGridWrapper,
    StyledHeader,
    StyledRomeo,
} from './Romeo.styled'
import {
    ResponsiveContainer,
    ResponsivePictureSizes,
} from '../../content/Picture/responsive'
import { useImpressionAvailable } from '../../__helpers/impression-available-helper'
import { useProduct } from '../../__product/useProduct'
import { ThemeMargins } from '../../__styling/settings/metrics'
import { invertMaybeLoadedItems } from '../helpers/loading'
import {
    PNSectionHeader,
    PNSectionHeaderProps,
} from '../../nextgen-news/perthnow/PNSectionHeader'
import {
    ImageMode,
    HeaderSectionTagType,
    PNArticleCard,
    PNFontScales,
    KickerMode,
    PNArticleCardProps,
} from '../../nextgen-news/perthnow/cards/PNArticleCard'
import { TeaserMode } from '../../cards/CardText/CardTeaser'
import debug from 'debug'

const romeoViewDebug = debug('romeo-impression-debug')

type InterlacedComponent = {
    /** The component to interlace. */
    component: (index: number) => ReactNode
    /** After how many cards the first instance of this component appears. Defaults to 1. */
    initialOffset?: number
    /** How many cards between each instance of this component. Defaults to 1. */
    cardsBetween?: number
    /** How many times to interlace this component. Defaults to 1. */
    repetitions?: number
}

export interface RomeoProps extends CommonCardProps, ResponsiveContainer {
    headerSpacing?: number | PNFontScales
    className?: string
    sectionHeader?: PNSectionHeaderProps
    showLargeCard?: boolean
    fixedRatios?: FixedRatio | FixedRatio[] | CardBreakpointRatios
    imageWidths?: ResponsivePictureSizes
    isTimestamped?: boolean
    verticalSpacing?: keyof ThemeMargins
    onEvent: (event: AllEvents) => void
    disableImageLazyLoad?: boolean
    items: MaybeLoaded<CardItem[]>
    expectedCards: number
    noHorizontalDividers?: boolean
    topicLevel?: 'parent'
    hideTopDivider?: boolean
    hideLastDivider?: boolean
    cardSpacing?: number
    hideOnMobile?: boolean
    fontScale?: PNFontScales
    teaserFontScale?: PNFontScales
    headerFontScale?: PNFontScales
    imageMode?: ImageMode
    teaserMode?: TeaserMode
    kickerMode?: KickerMode
    showAuthor?: boolean
    showCardTopic?: boolean
    hideCardFooter?: boolean
    horizontalDividerColor?: string
    displayOnBreakpoint?: DisplayOnBreakpoint
    marginTop?: number
    marginBottom?: number
    headerSectionTag?: HeaderSectionTagType
    topBottomDividerColorOverride?: string
    headerFontOverride?: PNArticleCardProps['headerFontOverride']
    imageSizeOverride?: PNArticleCardProps['imageSizeOverride']
    interlacedComponents?: Record<'advert' | string, InterlacedComponent>
    itemClickEventHandler?: (
        e: React.MouseEvent<HTMLElement>,
        item: MaybeLoaded<CardItem>,
        cardNumber: number,
    ) => void
    impressionEventInfo?: {
        collectionName: string
        overrides: Omit<CollectionAvailableEventProps, 'collectionCardCount'>
    }
}

export const Romeo: React.FC<RomeoProps> = ({
    items: maybeLoadedItems,
    className,
    sectionHeader,
    onEvent,
    expectedCards,
    noHorizontalDividers,
    hideTopDivider,
    hideLastDivider,
    topBottomDividerColorOverride,
    horizontalDividerColor,
    cardSpacing = 20,
    displayOnBreakpoint,
    marginTop,
    marginBottom,
    interlacedComponents = {},
    itemClickEventHandler,
    impressionEventInfo,
    teaserMode,
    teaserFontScale,
    fontScale,
    fixedRatios,
    headerFontOverride,
    headerFontScale,
    hideByline,
    hideCardFooter,
    disableImageLazyLoad,
    imageMode,
    showAuthor,
    showCardTopic,
    headerSectionTag,
    kickerMode,
    isTimestamped,
    imageSizeOverride,
    headerSpacing,
}) => {
    const product = useProduct()
    const items = invertMaybeLoadedItems(maybeLoadedItems, expectedCards)

    const buildList = () => {
        // Keep track of how many times left to repeat.
        const interlacedRepetitionsRemaining: Record<string, number> = {}

        for (const [id, interlacedComponent] of Object.entries(
            interlacedComponents,
        )) {
            // Display once if no repetitions specified.
            interlacedRepetitionsRemaining[id] =
                interlacedComponent.repetitions ?? 1
        }

        const listResult: ReactNode[] = []

        for (let i = 0; i < items.length; i++) {
            let shouldInsertDivider = true

            // Insert interlaced components.
            for (const [id, interlacedComponent] of Object.entries(
                interlacedComponents,
            )) {
                if (
                    isValidInterlaceIndex(
                        i,
                        interlacedComponent.initialOffset,
                        interlacedComponent.cardsBetween,
                    ) &&
                    interlacedRepetitionsRemaining[id] !== 0
                ) {
                    listResult.push(
                        <Fragment key={`${i}-${id}`}>
                            {interlacedComponent.component(i)}
                        </Fragment>,
                    )
                    interlacedRepetitionsRemaining[id]--
                    // Do not insert a divider if there is an interlaced component.
                    shouldInsertDivider = false
                }
            }

            // Insert card.
            const loadableItem = items[i]

            if (!loadableItem.loaded) {
                return null
            }

            const { result } = loadableItem

            if (result.cardType === 'marketing-redirect-tile') {
                // Do not render marketing redirects.
                continue
            } else {
                listResult.push(
                    <StyledGridItem
                        onClick={(event) =>
                            itemClickEventHandler &&
                            itemClickEventHandler(event, loadableItem, i)
                        }
                        key={i}
                        hideTopDivider={hideTopDivider ?? true}
                        horizontalDividerColor={horizontalDividerColor}
                        noHorizontalDividers={noHorizontalDividers === true}
                        hideLastDivider={hideLastDivider}
                        cardSpacing={cardSpacing}
                        topBottomDividerColorOverride={
                            topBottomDividerColorOverride
                        }
                    >
                        <PNArticleCard
                            key={result.id}
                            item={{ loaded: true, result }}
                            onEvent={onEvent}
                            cardNumber={i + 1}
                            cardType={'horizontal'}
                            teaserMode={teaserMode || 'hidden'}
                            fontScale={fontScale || 'S'}
                            teaserFontScale={
                                teaserFontScale || fontScale || 'S'
                            }
                            headerFontScale={
                                headerFontScale || fontScale || 'S'
                            }
                            hideByline={hideByline}
                            hideFooter={hideCardFooter}
                            fixedRatio={fixedRatios || ['4:3', '16:9']}
                            disableImageLazyLoad={disableImageLazyLoad}
                            imageMode={imageMode}
                            showAuthor={showAuthor}
                            showTopic={showCardTopic}
                            headerSectionTag={headerSectionTag}
                            kickerMode={kickerMode}
                            headerFontOverride={headerFontOverride}
                            imageSizeOverride={imageSizeOverride}
                            isTimestamped={isTimestamped}
                            headerSpacing={headerSpacing}
                        />
                    </StyledGridItem>,
                )
            }
        }

        return listResult
    }

    const numItems = items

    const impressionRef = useImpressionAvailable({
        loading: !maybeLoadedItems.loaded,
        available: () => {
            if (!maybeLoadedItems.loaded) {
                console.warn(
                    'Available should never be called when loading is true',
                )
                return
            }
            onEvent(
                createCollectionAvailableEvent(
                    maybeLoadedItems.result,
                    impressionEventInfo?.collectionName ?? 'Romeo',
                    product,
                    onEvent,
                    impressionEventInfo?.overrides,
                ),
            )
        },
        debug: romeoViewDebug,
    })

    return (
        <StyledRomeo
            ref={impressionRef}
            className={className}
            displayOnBreakpoint={displayOnBreakpoint}
            marginTop={marginTop}
            marginBottom={marginBottom}
        >
            {sectionHeader && items && (
                <StyledHeader>
                    <PNSectionHeader {...sectionHeader} onEvent={onEvent} />
                </StyledHeader>
            )}
            <StyledGridWrapper>
                {numItems.length > 0 && buildList()}
            </StyledGridWrapper>
        </StyledRomeo>
    )
}

/** Checks if a 0-indexed position is valid, provided an initial offset and spaces between. */
const isValidInterlaceIndex = (
    index: number,
    initialOffset = 1,
    spotsBetween = 1,
) =>
    // Sanity checks.
    initialOffset > 0 &&
    spotsBetween > 0 &&
    // Interlaced components can only be between cards.
    index !== 0 &&
    // Actual condition. Object.is() distinguishes between -0 and 0
    Object.is((index - initialOffset) % spotsBetween, 0)
